import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, Eye, Trash2, GripVertical, Plus, X, Monitor, Smartphone, ArrowLeft,
  Mail, Layers,
} from "lucide-react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const CATEGORIES = [
  { value: "transactional", label: "Transaccional" },
  { value: "marketing", label: "Marketing" },
  { value: "drip", label: "Drip / Trigger" },
];

const BLOCK_LABELS = {
  email_hero: "Hero",
  email_text: "Texto libre",
  email_button: "Botón CTA",
  email_image: "Imagen",
  email_divider: "Separador",
  email_program_card: "Programa (card)",
  email_stats_row: "Stats",
  email_footer: "Footer",
};

const uid = () => "b" + Math.random().toString(36).slice(2, 10);

export default function EmailTemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const base = superadmin ? "/app/superadmin" : "/app/staff";

  const [template, setTemplate] = useState(null);
  const [blockTypes, setBlockTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewMeta, setPreviewMeta] = useState(null);
  const [viewport, setViewport] = useState("desktop");
  const [selectedId, setSelectedId] = useState(null);
  const savingTimer = useRef(null);
  const lastSavedJson = useRef("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    setLoading(true);
    try {
      const [t, bt] = await Promise.all([
        api.get(`/admin/email-templates/${id}`),
        api.get("/admin/email-templates/_/block-types"),
      ]);
      setTemplate(t.data);
      setBlockTypes(bt.data.items || []);
      lastSavedJson.current = JSON.stringify(t.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      nav(`${base}/email/templates`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const saveDraft = async (silent = false) => {
    if (!template) return;
    setSaving(true);
    try {
      const payload = {
        name: template.name,
        subject_template: template.subject_template,
        preheader: template.preheader,
        blocks: template.blocks || [],
        category: template.category,
        is_active: template.is_active,
      };
      const r = await api.patch(`/admin/email-templates/${id}`, payload);
      lastSavedJson.current = JSON.stringify(r.data);
      if (!silent) toast.success("Guardado");
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const previewRender = async () => {
    await saveDraft(true);
    // Preview via campaña temporal — usar endpoint de preview con un user actual
    // Como no hay campaign_id, hacemos "preview inline" creando una temporal:
    try {
      // Truco: usamos POST /admin/email-campaigns con status=draft y luego preview + delete
      const c = await api.post("/admin/email-campaigns", {
        name: `preview-${Date.now()}`,
        template_id: id,
        status: "draft",
        send_type: "one_shot",
        segment: {},
      });
      const p = await api.post(`/admin/email-campaigns/${c.data.id}/preview`, { recipient_user_id: user?._id });
      setPreviewHtml(p.data.html);
      setPreviewMeta({ subject: p.data.subject, preheader: p.data.preheader, to: p.data.recipient_email });
      await api.delete(`/admin/email-campaigns/${c.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (!template) return;
    if (savingTimer.current) clearTimeout(savingTimer.current);
    const j = JSON.stringify(template);
    if (j !== lastSavedJson.current) {
      savingTimer.current = setTimeout(() => { saveDraft(true); }, 3000);
    }
    return () => savingTimer.current && clearTimeout(savingTimer.current);
    // eslint-disable-next-line
  }, [template]);

  const addBlock = (type) => {
    const bt = blockTypes.find((b) => b.type === type);
    if (!bt) return;
    const nb = { id: uid(), type, props: JSON.parse(JSON.stringify(bt.props || {})) };
    setTemplate((t) => ({ ...t, blocks: [...(t.blocks || []), nb] }));
    setSelectedId(nb.id);
  };

  const removeBlock = (bid) => {
    setTemplate((t) => ({ ...t, blocks: (t.blocks || []).filter((b) => b.id !== bid) }));
    if (selectedId === bid) setSelectedId(null);
  };

  const updateBlockProp = (bid, key, value) => {
    setTemplate((t) => ({
      ...t,
      blocks: (t.blocks || []).map((b) =>
        b.id === bid ? { ...b, props: { ...b.props, [key]: value } } : b
      ),
    }));
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setTemplate((t) => {
      const oldIx = t.blocks.findIndex((b) => b.id === active.id);
      const newIx = t.blocks.findIndex((b) => b.id === over.id);
      return { ...t, blocks: arrayMove(t.blocks, oldIx, newIx) };
    });
  };

  const selected = template?.blocks?.find((b) => b.id === selectedId);

  if (loading || !template) {
    return (
      <StaffLayout user={user} superadmin={superadmin}>
        <p className="text-center text-gray-400 py-12">Cargando editor…</p>
      </StaffLayout>
    );
  }

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div className="mb-4">
        <Link to={`${base}/email/templates`} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white">
          <ArrowLeft className="w-3 h-3" /> Volver a plantillas
        </Link>
      </div>

      {/* Top bar */}
      <div className="mb-4 p-4 rounded-xl border border-gray-800 bg-[#111827] flex flex-wrap items-center gap-3">
        <input
          value={template.name}
          onChange={(e) => setTemplate({ ...template, name: e.target.value })}
          data-testid="template-name-input"
          className="flex-1 min-w-[200px] bg-transparent border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
        />
        <select
          value={template.category}
          onChange={(e) => setTemplate({ ...template, category: e.target.value })}
          data-testid="template-category-select"
          className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
        >
          {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
        </select>
        <button
          onClick={() => saveDraft(false)}
          disabled={saving}
          data-testid="template-save-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-primary hover:brightness-110 text-primary-foreground text-sm font-semibold disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
        </button>
        <button
          onClick={previewRender}
          data-testid="email-editor-preview-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gray-800 text-white text-sm font-semibold hover:bg-white/5"
        >
          <Eye className="w-4 h-4" /> Preview
        </button>
      </div>

      {/* Subject + preheader */}
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <input
          value={template.subject_template}
          onChange={(e) => setTemplate({ ...template, subject_template: e.target.value })}
          placeholder="Asunto (usa {{first_name}}, {{tenant_name}}…)"
          data-testid="template-subject-input"
          className="bg-[#111827] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
        />
        <input
          value={template.preheader}
          onChange={(e) => setTemplate({ ...template, preheader: e.target.value })}
          placeholder="Preheader (texto de vista previa en la bandeja)"
          data-testid="template-preheader-input"
          className="bg-[#111827] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
        />
      </div>

      {/* 3 paneles */}
      <div className="grid lg:grid-cols-[220px_1fr_320px] gap-4">
        {/* PANEL IZQUIERDA · block types + stack */}
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-800 bg-[#111827] p-3">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Agregar bloque</p>
            <div className="space-y-1">
              {blockTypes.map((bt) => (
                <button
                  key={bt.type}
                  onClick={() => addBlock(bt.type)}
                  data-testid={`email-editor-add-block-${bt.type}`}
                  className="w-full text-left px-2 py-1.5 rounded text-xs text-gray-300 hover:bg-primary/10 hover:text-primary flex items-center gap-2"
                >
                  <Plus className="w-3 h-3" /> {bt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-gray-800 bg-[#111827] p-3">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 flex items-center gap-1">
              <Layers className="w-3 h-3" /> Estructura ({template.blocks?.length || 0})
            </p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={(template.blocks || []).map((b) => b.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1" data-testid="template-block-stack">
                  {(template.blocks || []).map((b) => (
                    <BlockStackItem
                      key={b.id}
                      block={b}
                      selected={selectedId === b.id}
                      onSelect={() => setSelectedId(b.id)}
                      onRemove={() => removeBlock(b.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* PANEL CENTRO · preview */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className="flex gap-1">
              <button
                onClick={() => setViewport("desktop")}
                className={`p-1.5 rounded ${viewport === "desktop" ? "bg-primary/20 text-primary" : "text-gray-400"}`}
                aria-label="Desktop"
                data-testid="preview-viewport-desktop"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewport("mobile")}
                className={`p-1.5 rounded ${viewport === "mobile" ? "bg-primary/20 text-primary" : "text-gray-400"}`}
                aria-label="Mobile"
                data-testid="preview-viewport-mobile"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
              Preview
            </p>
          </div>

          {/* Inbox mock header */}
          <div className="rounded-t-lg border border-b-0 border-gray-800 bg-[#111827] p-3">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400">Debt Capital College &lt;no-reply@dcc.mx&gt;</p>
                <p className="text-sm font-bold text-white truncate">
                  {template.subject_template || "Sin asunto"}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {template.preheader || "Sin preheader"}
                </p>
              </div>
            </div>
          </div>

          <div
            className="border border-gray-800 rounded-b-lg bg-[#0B0F14] overflow-hidden"
            style={{ minHeight: 500 }}
          >
            {previewHtml ? (
              <iframe
                title="email-preview"
                srcDoc={previewHtml}
                data-testid="email-preview-iframe"
                className="bg-white block"
                style={{
                  width: viewport === "mobile" ? 375 : "100%",
                  maxWidth: "100%",
                  height: 700,
                  margin: viewport === "mobile" ? "0 auto" : "0",
                  border: "none",
                }}
              />
            ) : (
              <div className="p-6 flex flex-col items-center justify-center text-gray-500 text-sm space-y-3" style={{ minHeight: 500 }}>
                <Eye className="w-10 h-10 opacity-40" />
                <p>Haz clic en "Preview" para renderizar el email</p>
                <p className="text-xs">Los cambios se auto-guardan cada 3 segundos.</p>
              </div>
            )}
          </div>
        </div>

        {/* PANEL DERECHA · props form */}
        <div className="rounded-xl border border-gray-800 bg-[#111827] p-4">
          {!selected ? (
            <div className="text-center text-gray-500 py-12">
              <Plus className="w-6 h-6 mx-auto opacity-40 mb-2" />
              <p className="text-xs">Selecciona un bloque para editar</p>
            </div>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold mb-3">
                {BLOCK_LABELS[selected.type] || selected.type}
              </p>
              <BlockPropsForm
                block={selected}
                onChange={(k, v) => updateBlockProp(selected.id, k, v)}
              />
            </>
          )}
        </div>
      </div>
    </StaffLayout>
  );
}

function BlockStackItem({ block, selected, onSelect, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs cursor-pointer ${
        selected ? "bg-primary/20 text-primary" : "hover:bg-white/5 text-gray-300"
      }`}
      data-testid={`stack-item-${block.type}`}
    >
      <span {...attributes} {...listeners} className="cursor-grab text-gray-500">
        <GripVertical className="w-3 h-3" />
      </span>
      <span className="flex-1 truncate">{BLOCK_LABELS[block.type] || block.type}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="text-gray-500 hover:text-destructive"
        aria-label="Eliminar"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function BlockPropsForm({ block, onChange }) {
  const p = block.props || {};
  const F = (label, key, opts = {}) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      <input
        value={p[key] ?? ""}
        onChange={(e) => onChange(key, e.target.value)}
        placeholder={opts.placeholder || ""}
        type={opts.type || "text"}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary"
      />
    </label>
  );
  const TX = (label, key, rows = 3) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      <textarea
        value={p[key] ?? ""}
        onChange={(e) => onChange(key, e.target.value)}
        rows={rows}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary"
      />
    </label>
  );
  const CK = (label, key) => (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={!!p[key]}
        onChange={(e) => onChange(key, e.target.checked)}
        className="w-4 h-4 accent-primary"
      />
      <span className="text-xs text-white">{label}</span>
    </label>
  );
  const SEL = (label, key, options) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      <select
        value={p[key] ?? options[0]}
        onChange={(e) => onChange(key, e.target.value)}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white"
      >
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  );

  switch (block.type) {
    case "email_hero":
      return (<div className="space-y-3">
        {F("Título", "title", { placeholder: "Bienvenido, {{first_name}}" })}
        {F("Subtítulo (eyebrow)", "subtitle")}
        {F("Imagen (URL)", "image_url")}
        {F("Texto del CTA", "cta_text")}
        {F("Link del CTA", "cta_link")}
      </div>);
    case "email_text":
      return (<div className="space-y-3">
        {TX("Contenido (markdown lite)", "body_md", 8)}
        {SEL("Alineación", "align", ["left", "center"])}
      </div>);
    case "email_button":
      return (<div className="space-y-3">
        {F("Texto", "text")}
        {F("URL", "url")}
        {SEL("Alineación", "align", ["left", "center", "right"])}
        {SEL("Tamaño", "size", ["sm", "md", "lg"])}
      </div>);
    case "email_image":
      return (<div className="space-y-3">
        {F("URL imagen", "image_url")}
        {F("Alt", "alt")}
        {F("Link (opcional)", "url")}
      </div>);
    case "email_divider":
      return (<div className="space-y-3">
        {SEL("Estilo", "style", ["solid", "dashed", "dotted"])}
        {F("Color (hex)", "color")}
      </div>);
    case "email_program_card":
      return (<div className="space-y-3">
        {F("Program slug", "program_slug", { placeholder: "high-performance-client-management-hpcm" })}
        {CK("Mostrar precio", "show_price")}
      </div>);
    case "email_stats_row":
      return (<div className="space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Items (JSON)</p>
        <textarea
          value={JSON.stringify(p.items || [], null, 2)}
          onChange={(e) => { try { onChange("items", JSON.parse(e.target.value)); } catch (_) { /* ignore invalid */ } }}
          rows={6}
          className="w-full bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white font-mono"
        />
      </div>);
    case "email_footer":
      return (<div className="space-y-3">
        {TX("Texto de baja", "unsubscribe_text", 2)}
        {F("Dirección postal", "address")}
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Links (JSON)</p>
        <textarea
          value={JSON.stringify(p.links || [], null, 2)}
          onChange={(e) => { try { onChange("links", JSON.parse(e.target.value)); } catch (_) {} }}
          rows={4}
          className="w-full bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white font-mono"
        />
      </div>);
    default:
      return <p className="text-xs text-gray-500">Sin editor para este tipo.</p>;
  }
}
